import * as React from 'react';
import { observer } from 'mobx-react';
import { Select, Option } from '@payscale/design';
import AssignUserView from 'components/Configuration/assignUserView';
import AssignUserModalView from 'components/Configuration/assignUserModalView';
import { BaseEmployeeLevel } from 'nomst/Stores/Configuration/baseEmployeeLevel';
import { AssignedUserLevel, EmployeeNameFormat } from 'typings/enums';
import HrRolesReviewerLink from 'components/WorkflowConfiguration/hrRolesReviewerLink';
import { AssignUserViewStore } from 'nomst/Stores/Configuration/assignUserView.store';
import { RemoveUserDialog } from 'components/Common/removeUserDialog';
import { ConfigTabs, EmployeeLevelEnum, FeatureFlags } from 'typings/enums';
import { workflowSettingsViewStore, configurationsStore } from 'utils/MobxDataManager/singletons';
import DataSummary from 'nomst/Components/Configuration/dataSummary';
import { featureFlagHelper } from 'singletons';
import AccessGroupOption from './accessGroupOption';
import './globalConfigurationView.scss';

export interface IGlobalConfigurationViewProps {
    readonly: false;
}

interface IGlobalConfigurationViewState {
    selectedLevel: EmployeeLevelEnum;
    shouldShowAlertText: boolean;
}

const levelLabels: Map<EmployeeLevelEnum, string> = new Map<EmployeeLevelEnum, string>([
    [EmployeeLevelEnum.Division, 'Division'],
    [EmployeeLevelEnum.Department, 'Department'],
    [EmployeeLevelEnum.BusinessUnit, 'Business Unit']]);

export class GlobalConfigurationView
    extends React.Component<IGlobalConfigurationViewProps, IGlobalConfigurationViewState> {
    constructor(props: IGlobalConfigurationViewProps) {
        super(props);
        this.handleSaveConfiguration = this.handleSaveConfiguration.bind(this);
        this.handleChangeEmployeeNameFormat = this.handleChangeEmployeeNameFormat.bind(this);
        this.handleChangeEmployeeNameSortOrder = this.handleChangeEmployeeNameSortOrder.bind(this);
        this.handleSetPercentageNumberAfterDecimal = this.handleSetPercentageNumberAfterDecimal.bind(this);
        this.handleSetHourlyNumberAfterDecimal = this.handleSetHourlyNumberAfterDecimal.bind(this);
        this.handleSetAnnualNumberAfterDecimal = this.handleSetAnnualNumberAfterDecimal.bind(this);
        this.handleSetStockNumberAfterDecimal = this.handleSetStockNumberAfterDecimal.bind(this);
        this.setEmployeeTab = this.setEmployeeTab.bind(this);
        this.setHrbpTab = this.setHrbpTab.bind(this);
        this.setDataTab = this.setDataTab.bind(this);
        this.setDepartmentTab = this.setDepartmentTab.bind(this);
        this.setDivisionTab = this.setDivisionTab.bind(this);
        this.setBusinessUnitTab = this.setBusinessUnitTab.bind(this);
        this.toggleSelectedLevel = this.toggleSelectedLevel.bind(this);
        this.unsetAllSelections = this.unsetAllSelections.bind(this);
        this.showModal = this.showModal.bind(this);

        this.state = {
            selectedLevel: EmployeeLevelEnum.Department,
            shouldShowAlertText: false
        };
    }

    componentDidMount(): Promise<void> {
        this.hideAlertText();
        return configurationsStore.getHrReviewers();
    }

    render(): React.ReactNode {
        return (
            <div className="config config--global" id="global-config">
                <div className="global-configuration-sidebar">
                    {this.renderSidebar()}
                </div>
                <div className="global-configuration-page">
                    {this.renderSettingsPage()}
                </div>
            </div>
        );
    }

    private renderSidebar(): React.ReactNode {
        return (
            <>
                <h3
                    className="pxl-margin-bottom-sm"
                >
                    Global Configuration
                </h3>
                <ul className="config-menu">
                    <li>
                        <button
                            onClick={this.setHrbpTab}
                            className={this.getSelectedClassName(ConfigTabs.HRBP)}
                        >
                            HR Business Partner
                        </button>
                    </li>
                    <li>
                        <button
                            onClick={this.setEmployeeTab}
                            className={this.getSelectedClassName(ConfigTabs.EMPLOYEE)}
                        >
                            Employee Settings
                        </button>
                    </li>
                    {featureFlagHelper.featureFlagSettings[FeatureFlags.ShowDataSummary] && 
                    <li>
                        <button
                            onClick={this.setDataTab}
                            className={this.getSelectedClassName(ConfigTabs.DATA)}
                        >
                            Team Data Summary
                        </button>
                    </li>}
                </ul>
            </>
        );
    }

    private renderGlobalSettings(): React.ReactNode {
        return (
            <div>
                <h1
                    className="pxl-header-sm"
                >
                    Global Settings
                </h1>
                <div className="form-row">
                    {this.renderGlobalSettingsFormatConfiguration()}
                    {this.renderGlobalSettingsGeneralConfiguration()}
                </div>
                <div className="pxl-text-right">
                    {this.renderChangesHasBeenSavedAlert()}
                    <button
                        className="pxl-btn pxl-btn--primary"
                        onClick={this.handleSaveConfiguration}
                    >
                        Save
                    </button>
                </div>
            </div>
        );
    }

    private renderChangesHasBeenSavedAlert(): React.ReactNode {
        if (!this.state.shouldShowAlertText) {
            return null;
        }

        return (
            <span
                id="alert"
                className="pxl-margin-right-lg"
            >
                <i
                    className="icon-check pxl-text-c9"
                />
                &nbsp;Global Settings changes saved
            </span>
        );
    }

    private async handleSaveConfiguration(): Promise<void> {
        await configurationsStore.saveConfiguration();
        this.setState({shouldShowAlertText: true});
    }

    private renderGlobalSettingsFormatConfiguration(): React.ReactNode {
        const employeeFormatConfiguration = configurationsStore._employeeConfiguration.formatConfiguration;
        const formatConfiguration = configurationsStore._configuration.formatConfiguration;
        return (
            <div className="format-configuration">
                <legend>Format Configuration</legend>
                <div className="format-configuration-row">
                    <label htmlFor="nameFormatSelect" className="format-configuration-row__header">Configure the format for employee name</label>
                    <div className="format-configuration-row__select">
                        <Select
                            id="nameFormatSelect"
                            value={employeeFormatConfiguration.employeeNameSortFormat}
                            onChange={this.handleChangeEmployeeNameFormat}
                            options={employeeFormatConfiguration.employeeNameFormatOptions}
                        />
                    </div>
                </div>
                <div className="format-configuration-row">
                    <label htmlFor="orderFormatSelect" className="format-configuration-row__header">Configure set order format for employee name</label>
                    <div className="format-configuration-row__select">
                        <Select
                            id="orderFormatSelect"
                            value={formatConfiguration.employeeNameSortFormat}
                            onChange={this.handleChangeEmployeeNameSortOrder}
                            options={formatConfiguration.employeeNameFormatOptions}
                        />
                    </div>
                </div>
            </div>
        );
    }

    private handleChangeEmployeeNameFormat(_: undefined, value: EmployeeNameFormat): void {
        const formatConfiguration = configurationsStore._employeeConfiguration.formatConfiguration;
        formatConfiguration.setEmployeeNameSortFormat(value);
        this.hideAlertText();
    }

    private handleChangeEmployeeNameSortOrder(_: undefined, value: EmployeeNameFormat): void {
        const formatConfiguration = configurationsStore._configuration.formatConfiguration;
        formatConfiguration.setEmployeeNameSortFormat(value);
        this.hideAlertText();
    }

    private renderGlobalSettingsGeneralConfiguration(): React.ReactNode {
        const generalConfiguration = configurationsStore._configuration.generalConfiguration;
        return (
            <fieldset>
                <legend>General Configuration</legend>
                <label htmlFor="number-formatting">Number Formatting</label>
                <table id="number-formatting" className="config__table-number-formatting">
                    <thead>
                        <tr>
                            <th />
                            <th>Percentage</th>
                            <th>Hourly</th>
                            <th>Annual</th>
                            <th>Stock</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <th>Decimal</th>
                            <td>
                                <Select
                                    id="decimalPercentageSelect"
                                    disabled={false}
                                    value={generalConfiguration.percentageNumberAfterDecimal}
                                    onChange={this.handleSetPercentageNumberAfterDecimal}
                                    options={generalConfiguration.numberAfterDecimalOptions}
                                />
                            </td>
                            <td>
                                <Select
                                    id="decimalHourlySelect"
                                    disabled={false}
                                    value={generalConfiguration.hourlyNumberAfterDecimal}
                                    onChange={this.handleSetHourlyNumberAfterDecimal}
                                    options={generalConfiguration.numberAfterDecimalOptions}
                                />
                            </td>
                            <td>
                                <Select
                                    id="decimalAnnualSelect"
                                    disabled={false}
                                    value={generalConfiguration.annualNumberAfterDecimal}
                                    onChange={this.handleSetAnnualNumberAfterDecimal}
                                    options={generalConfiguration.numberAfterDecimalOptions}
                                />
                            </td>
                            <td>
                                <Select
                                    id="decimalStockSelect"
                                    disabled={false}
                                    value={generalConfiguration.stockNumberAfterDecimal}
                                    onChange={this.handleSetStockNumberAfterDecimal}
                                    options={generalConfiguration.numberAfterDecimalOptions}
                                />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </fieldset>
        );
    }

    private hideAlertText(): void {
        this.setState({shouldShowAlertText: false});
    }

    private handleSetPercentageNumberAfterDecimal(_: undefined, value: number): void {
        const generalConfiguration = configurationsStore._configuration.generalConfiguration;
        generalConfiguration.setPercentageNumberAfterDecimal(+value);
        this.hideAlertText();
    }

    private handleSetHourlyNumberAfterDecimal(_: undefined, value: number): void {
        const generalConfiguration = configurationsStore._configuration.generalConfiguration;
        generalConfiguration.setHourlyNumberAfterDecimal(+value);
        this.hideAlertText();
    }

    private handleSetAnnualNumberAfterDecimal(_: undefined, value: number): void {
        const generalConfiguration = configurationsStore._configuration.generalConfiguration;
        generalConfiguration.setAnnualNumberAfterDecimal(+value);
        this.hideAlertText();
    }

    private handleSetStockNumberAfterDecimal(_: undefined, value: number): void {
        const generalConfiguration = configurationsStore._configuration.generalConfiguration;
        generalConfiguration.setStockNumberAfterDecimal(+value);
        this.hideAlertText();
    }

    private setEmployeeTab(): void {
        configurationsStore.setConfigurationTab(ConfigTabs.EMPLOYEE);
        configurationsStore.navigateConfigurationView();
    }

    private setHrbpTab(): void {
        configurationsStore.setConfigurationTab(ConfigTabs.HRBP);
        configurationsStore.navigateConfigurationView();
    }

    private setDataTab(): void {
        configurationsStore.setConfigurationTab(ConfigTabs.DATA);
        configurationsStore.navigateConfigurationView();
    }

    private getSelectedClassName(tab: ConfigTabs): string {
        return 'pxl-link' + (this.isSelected(tab) ? ' selected' : '');
    }

    private isSelected(tab: ConfigTabs): boolean {
        const { configurationTab } = configurationsStore;
        return tab === configurationTab;
    }

    private renderSettingsPage(): React.ReactNode {
        const { configurationTab } = configurationsStore;
        switch (configurationTab) {
            case ConfigTabs.EMPLOYEE:
                return this.renderGlobalSettings();
            case ConfigTabs.DATA:
                return this.renderDataSummary();
            case ConfigTabs.HRBP:
            default:
                return (
                    <div>
                        {/* TODO replace div with <React.Fragment> when React 16.2+ */}
                        {this.renderHrReviewersSection()}
                        {this.renderRemoveAssignedUserWarningBox()}
                    </div>
                );
        }
    }

    private renderHrReviewersSection(): React.ReactNode {
        const isHidden = this.props.readonly || configurationsStore.anySelectedGroup === false;
        return (
            <section className="config-content">
                <h1 className="config-content__heading">HR Business Partner</h1>
                <h3 className="config-content__subheading">Assign Reviewers</h3>
                <div className="config-content__intro-container">
                    <p className="config-content__intro-text">
                        Views assigned group org, sends teams within group for review and can edit configurations.
                        Only one user can be assigned to an access group.
                    </p>
                    <button
                        onClick={this.showModal}
                        className={`config-content__btn pxl-btn pxl-btn--primary ${isHidden ? 'pxl-btn--is-hidden' : ''}`}
                    >
                        Assign User to Selected Groups
                    </button>
                </div>
                <AssignUserModalView
                    store={configurationsStore.assignUserHrReviewer}
                    searchAllUserTypes={true}
                />
                <nav className="tabs tabs--condensed">
                    {this.renderTab(EmployeeLevelEnum.Department, this.setDepartmentTab)}
                    {this.renderTab(EmployeeLevelEnum.Division, this.setDivisionTab)}
                    {this.renderTab(EmployeeLevelEnum.BusinessUnit, this.setBusinessUnitTab)}
                </nav>
                <table className="table--user-assignment config__table-hrbp-reviewer">
                    <thead>
                        <tr>
                            <th>
                                {this.renderAccessGroupOption(
                                    'AccessGroup',
                                    'Access Group',
                                    configurationsStore.anySelectedGroup,
                                    this.toggleSelectedLevel
                                )}
                            </th>
                            <th colSpan={2}>Assigned User (1 per group)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {this.renderHrReviewers()}
                    </tbody>
                </table>
            </section>
        );
    }

    private showModal(): void {
        configurationsStore.assignUserHrReviewer.showModal();
    }

    private renderTab(tab: EmployeeLevelEnum, onClick: () => void): React.ReactNode {
        const { selectedLevel } = this.state;
        return (
            <button
                className={`tab${selectedLevel === tab ? ' active' : ''} config-content--${tab.toLowerCase()} pxl-link`}
                onClick={onClick}
            >
                {levelLabels.get(tab)}
            </button>
        );
    }

    private renderDataSummary(): React.ReactNode {
        return <div className="global-configuration-page__data-summary">
            <DataSummary />
        </div>;
    }

    private renderHrReviewers(): React.ReactNode {
        const { selectedLevel } = this.state;

        return configurationsStore
            .employeeLevels
            .filter((hrReviewer: BaseEmployeeLevel) => hrReviewer.level === selectedLevel)
            .map((hrReviewer: BaseEmployeeLevel, index) => (
                <tr key={index}>
                    <td>
                        <AccessGroupOption
                            hrReviewer={hrReviewer}
                            readonly={this.props.readonly}
                        />
                    </td>
                    <AssignUserView
                        assignUserViewStore={hrReviewer.hrReviewer}
                        readonly={this.props.readonly}
                        includeAllUsers={true}
                    />
                    {
                        this.renderReviewerLink(
                            hrReviewer.hrReviewer,
                            AssignedUserLevel.HrReviewer,
                            hrReviewer.levelName)
                    }
                </tr>
            ));
    }

    private renderAccessGroupOption(
        id: string,
        text: string,
        checked: boolean,
        onChange: () => void): React.ReactNode {
        if (this.props.readonly) {
            return (
                <span>{text}</span>
            );
        }

        return (
            <Option
                id={id}
                text={text}
                checked={checked}
                onChange={onChange}
                className="pxl-option--secondary"
            />
        );
    }

    private renderReviewerLink(
        assignUserViewStore: AssignUserViewStore,
        assignedUserLevel: AssignedUserLevel,
        accessGroupDisplayName?: string): React.ReactNode {
        if (!assignUserViewStore.isUserAssigned) {
            return null;
        }

        return (
            <HrRolesReviewerLink
                assignUserViewStore={assignUserViewStore}
                assignedUserLevel={assignedUserLevel}
                accessGroupDisplayName={accessGroupDisplayName}
                readonly={this.props.readonly}
            />
        );
    }

    private renderRemoveAssignedUserWarningBox(): React.ReactNode {
        const removeAssignedUserModalView = workflowSettingsViewStore.removeAssignedUserStore;
        if (!removeAssignedUserModalView ||
            !removeAssignedUserModalView._assignedUserId) {
            return null;
        }

        return (
            <RemoveUserDialog />
        );
    }

    private setDepartmentTab(): void {
        this.unsetAllSelections();
        this.setState({ selectedLevel: EmployeeLevelEnum.Department });
    }

    private setDivisionTab(): void {
        this.unsetAllSelections();
        this.setState({ selectedLevel: EmployeeLevelEnum.Division });
    }

    private setBusinessUnitTab(): void {
        this.unsetAllSelections();
        this.setState({ selectedLevel: EmployeeLevelEnum.BusinessUnit });
    }

    private unsetAllSelections(): void {
        configurationsStore.unsetAllSelections();
    }

    private toggleSelectedLevel(): void {
        const level = levelLabels.get(this.state.selectedLevel)?.replace(' ', '');
        if (!level) {
            return;
        }
        configurationsStore.toggleSelectionsForLevel(level);
    }
}

export default observer(GlobalConfigurationView);
